/**
 * Single projection path for session-derived workspace state.
 * Input sessions are mentorApi.normalizeSession() shape.
 */

export function sessionRolesKey(sessionId, cohortId = null) {
  const sid = String(sessionId);
  return cohortId ? `${sid}:${String(cohortId)}` : sid;
}

export function resolveNominationOutcomeForCohort(session, cohortId = null) {
  if (!session) return { nomination: null, outcome: null };
  if (session.groupId && cohortId) {
    return {
      nomination: session.nominationsByCohort?.[cohortId] || null,
      outcome: session.outcomesByCohort?.[cohortId] || null,
    };
  }
  return {
    nomination: session.nomination || null,
    outcome: session.outcome || null,
  };
}

export function projectRolesFromSession(session, cohortId = null) {
  if (!session) return null;
  const { nomination, outcome } = resolveNominationOutcomeForCohort(session, cohortId);
  if (!nomination && !outcome) return null;
  return {
    chairId: nomination?.chairId || '',
    presenterIds: nomination?.presenterIds || [],
    mentorIds: nomination?.mentorIds || [],
    challengerIds: outcome?.challengerIds || [],
    bestPresenterId: outcome?.bestPresenterId || '',
    bestMentorId: outcome?.bestMentorId || '',
    bestChallengerId: outcome?.bestChallengerId || '',
    chairScore: outcome?.chairScore != null ? String(outcome.chairScore) : '',
  };
}

export function projectRoomAssignmentsFromSession(session) {
  if (!session?.auditorAssignments?.length) return null;
  const rooms = {};
  session.auditorAssignments.forEach((assignment, index) => {
    const roomNumber = assignment.roomNumber ?? index + 1;
    rooms[roomNumber] = assignment.auditorId;
  });
  return rooms;
}

export function projectAuditorStatusFromSession(session) {
  const assignment = (session?.auditorAssignments || [])[0];
  if (!assignment) return null;
  return assignment.assignmentStatus || 'pending';
}

const emptyChallengerEvalSlot = () => ({
  studentId: '',
  positiveKeywords: [],
  negativeKeywords: [],
  comment: '',
});

/** Seed auditor eval challenger picks from SPOC-logged session outcomes. */
export function buildEvaluationChallengerSlots(challengerIds = []) {
  const ids = (challengerIds || []).map(id => String(id)).filter(Boolean);
  const challengers = ids.slice(0, 3).map(studentId => ({
    ...emptyChallengerEvalSlot(),
    studentId,
  }));
  while (challengers.length < 3) {
    challengers.push(emptyChallengerEvalSlot());
  }
  const show4thChallenger = ids.length > 3;
  if (show4thChallenger) {
    challengers.push({
      ...emptyChallengerEvalSlot(),
      studentId: ids[3],
    });
  }
  return { challengers, show4thChallenger };
}

export function evaluationChallengersAreEmpty(challengers = []) {
  return (challengers || []).every(slot => !slot?.studentId);
}

export function applySessionProjections(sessionId, session, {
  setSessions,
  setSessionRoles,
  setSessionRoomAssignments,
  setAuditorSessionStatuses,
  setSelectedSession,
  forAuditorRole = false,
}) {
  const id = String(sessionId);

  setSessions(prev => {
    const exists = prev.some(s => String(s.id) === id);
    if (exists) {
      return prev.map(s => (String(s.id) === id ? session : s));
    }
    return [...prev, session];
  });

  const roles = projectRolesFromSession(session);
  setSessionRoles(prev => {
    const next = { ...prev };
    if (!roles) {
      delete next[id];
    } else {
      next[id] = roles;
    }
    return next;
  });

  const rooms = projectRoomAssignmentsFromSession(session);
  setSessionRoomAssignments(prev => {
    const next = { ...prev };
    if (!rooms) {
      delete next[id];
    } else {
      next[id] = rooms;
    }
    return next;
  });

  if (forAuditorRole) {
    const status = projectAuditorStatusFromSession(session);
    if (status) {
      setAuditorSessionStatuses(prev => ({ ...prev, [id]: status }));
    }
  }

  setSelectedSession(prev => (prev && String(prev.id) === id ? session : prev));
}